import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import { useAuth } from '../contexts/AuthContext';
import Logger from '../utils/logger';
import Logo from '../components/Logo';
import { Button, Input } from '../components/ui';
import { SuccessMessages } from '../constants/messages';

/**
 * Email-based device verification page (GitLab-style step-up auth).
 *
 * Reached via /login/verify-device after a successful POST /auth/login that
 * returned `requires_device_verification: true`. The pending verify_token is
 * stashed in sessionStorage by AuthContext.login(); we collect the 6-digit
 * code received by email and call completeDeviceVerification.
 *
 * The user may opt in "Trust this device for 30 days" via a checkbox — on
 * success, the backend posts a long-lived HttpOnly cookie that lets future
 * logins skip the email verification.
 */
const DeviceVerifyPage: React.FC = () => {
  const { t } = (useTranslation as any)('auth');
  const {
    completeDeviceVerification,
    resendDeviceVerificationCode,
    hasPendingDeviceVerification,
    getDeviceVerifyEmailHint,
    cancelDeviceVerification,
  } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [remember, setRemember] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!hasPendingDeviceVerification()) {
    return <Navigate to="/login" replace />;
  }

  const emailHint = getDeviceVerifyEmailHint();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await completeDeviceVerification(code.trim(), remember);
      toast.success(SuccessMessages.LOGIN_SUCCESS);
      navigate('/dashboard', { replace: true });
    } catch (err: unknown) {
      Logger.error('Device verification failed', err);
      const response = (err as { response?: { status?: number; data?: { detail?: string } } })
        ?.response;
      const detail = response?.data?.detail;
      // Backend renvoie le code error specifique : invalid_code, expired,
      // max_attempts, invalid_token. Mapper sur i18n.
      if (detail === 'invalid_code') {
        setError(t('deviceVerify.invalidCode'));
      } else if (detail === 'expired') {
        setError(t('deviceVerify.expiredCode'));
      } else if (detail === 'max_attempts') {
        setError(t('deviceVerify.tooManyAttempts'));
      } else {
        setError(t('deviceVerify.verificationFailed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onResend = async () => {
    setResending(true);
    setError(null);
    try {
      await resendDeviceVerificationCode();
      toast.success(t('deviceVerify.resendSuccess'));
      setCode('');
    } catch (err: unknown) {
      Logger.error('Device code resend failed', err);
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      if (detail === 'rate_limit_exceeded' || (err as { response?: { status?: number } })?.response?.status === 429) {
        toast.error(t('deviceVerify.resendRateLimited'));
      } else {
        toast.error(t('deviceVerify.resendError'));
      }
    } finally {
      setResending(false);
    }
  };

  const onCancel = () => {
    cancelDeviceVerification();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-surface-0 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <Logo size="large" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-text-primary">
          {t('deviceVerify.title')}
        </h2>
        <p className="mt-2 text-center text-sm text-text-secondary">
          {emailHint
            ? t('deviceVerify.subtitleWithEmail', { email: emailHint })
            : t('deviceVerify.subtitle')}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-surface-2 py-8 px-4 shadow-card sm:rounded-card sm:px-10">
          <form className="space-y-6" onSubmit={onSubmit}>
            <Input
              id="device-verify-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              maxLength={6}
              label={t('deviceVerify.codeLabel') as string}
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              error={error ?? undefined}
              className="text-center tracking-widest text-lg font-mono"
            />

            <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="w-4 h-4 rounded border-border-1 text-primary focus:ring-primary"
              />
              <span>{t('deviceVerify.rememberDevice')}</span>
            </label>

            <div className="flex flex-col gap-2">
              <Button
                type="submit"
                variant="primary"
                size="md"
                loading={submitting}
                disabled={submitting || code.length !== 6}
                className="w-full"
              >
                {t('deviceVerify.verifyButton')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="md"
                onClick={onResend}
                loading={resending}
                disabled={submitting || resending}
                className="w-full"
              >
                {t('deviceVerify.resendButton')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="md"
                onClick={onCancel}
                className="w-full"
                disabled={submitting || resending}
              >
                {t('deviceVerify.cancelButton')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DeviceVerifyPage;
